import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { authService } from "../../services/api";
import { useState } from "react";
import { Mail, Lock, Eye, EyeOff, FileText } from "lucide-react";
import toast from "react-hot-toast";
import { PRODUCT_NAME } from "../../config/brand";
import { useSignInInstitution } from "../../hooks/useSignInInstitution";
import { SupportContactView } from "../../components/support/SupportContactLine";
import StaffMfa from "../../components/auth/StaffMfa";

// Important PDF Guides — small static quick-start manuals served straight
// from public/guides (no auth needed; visible before sign-in by design).
const pdfGuides = [
  { href: "/guides/student-portal-guide.pdf",    label: "Student portal guide" },
  { href: "/guides/teacher-evaluator-guide.pdf", label: "Teacher evaluator guide" },
  { href: "/guides/offline-exam-scan-guide.pdf", label: "Offline exam scan guide" },
];

const Login = () => {
  const [formData, setFormData]     = useState({ identifier: "", password: "", institutionCode: "" });
  // Shown only after the server says this number exists in more than one
  // institution — everyone else never sees the field.
  const [needsInstitution, setNeedsInstitution] = useState(false);
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // A teacher who holds exam personas, where staff must give a second factor
  // (W3.1): the password answered a pending token, not a session.
  const [mfa, setMfa] = useState(null);
  // Whose sign-in page this is — from the page's address; null = product only.
  const named = useSignInInstitution();
  // "Not your institution?" (4.6): the page stops speaking for the institution
  // its address names and asks for the candidate's own institution code, which
  // the server takes over the address (tenancy/host.js).
  const [notMine, setNotMine] = useState(false);
  const institution = notMine ? null : named;
  const askInstitution = needsInstitution || notMine;
  const navigate = useNavigate();
  const { login }  = useAuth();
  const identifierHasEmail  = (formData.identifier || "").includes("@");
  const emailLoginBlockedMessage = "Email login is not allowed. Use Roll No / Enrolment No / Employee ID.";

  const signedIn = (session) => {
    login(session);
    const savedPath = localStorage.getItem("redirectAfterLogin");
    localStorage.removeItem("redirectAfterLogin");
    let redirectPath;
    if (savedPath && savedPath !== "/" && savedPath !== "/login") {
      redirectPath = savedPath;
    } else if (session.user.role === "admin") {
      redirectPath = "/admin";
    } else {
      redirectPath = session.user.role === "teacher" ? "/teacher/dashboard" : "/student/dashboard";
    }
    toast.success("Signed in successfully");
    navigate(redirectPath);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const identifier = (formData.identifier || "").trim();
    if (!identifier || !formData.password) { toast.error("Please fill in all fields"); return; }
    if (identifierHasEmail) { setError(emailLoginBlockedMessage); toast.error(emailLoginBlockedMessage); return; }
    if (identifier.length < 3) { toast.error("Enter your User ID"); return; }
    setLoading(true);
    setError("");
    try {
      const response = await authService.login(
        identifier,
        formData.password,
        askInstitution ? formData.institutionCode.trim().toUpperCase() || undefined : undefined
      );
      if (response.data?.mfaRequired && response.data?.mfaToken) setMfa(response.data);
      else if (response.data) signedIn(response.data);
    } catch (err) {
      const code = err?.response?.data?.code;
      let message = err?.response?.data?.message || err?.response?.data?.error || "Login failed. Please try again.";
      // One answer for a wrong User ID or a wrong password (A1): the server
      // no longer says which, so neither does this page.
      if (code === "INVALID_CREDENTIALS") message = "User ID or password is wrong";
      if (code === "AMBIGUOUS_IDENTIFIER") {
        setNeedsInstitution(true);
        message = "This number is used by more than one institution — enter your institution code.";
      }
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (mfa) {
    return (
      <StaffMfa
        challenge={mfa}
        onDone={(session) => {
          setMfa(null);
          signedIn(session);
        }}
        onCancel={() => {
          setMfa(null);
          setFormData({ ...formData, password: "" });
        }}
      />
    );
  }

  return (
    <div className="min-h-screen w-screen flex flex-col lg:flex-row font-sans bg-white">

      {/* ── Left: the institution (when the address names one) and the product ── */}
      <div
        className="relative hidden lg:flex lg:w-1/2 overflow-hidden"
        style={{ background: "linear-gradient(150deg, #312e81 0%, #4338ca 50%, #6d28d9 100%)" }}
      >
        <div className="relative z-10 flex flex-col justify-between w-full p-12 text-white">
          {institution?.logoUrl ? (
            <img
              src={institution.logoUrl}
              alt={institution.name}
              className="h-12 w-auto self-start object-contain bg-white/95 rounded-lg p-1.5"
            />
          ) : (
            <span className="text-lg font-semibold tracking-tight" data-testid="login-product-mark">{PRODUCT_NAME}</span>
          )}

          <div className="max-w-lg">
            <h2
              className="text-5xl font-extrabold leading-[1.08] drop-shadow-sm"
            >
              Exams that keep
              <br />
              every answer safe.
            </h2>
            <p className="text-base text-white/90 mt-5 leading-relaxed">
              {institution ? `${institution.name} · ` : ""}{PRODUCT_NAME}
            </p>
          </div>

          <p className="text-xs text-white/70">
            © {new Date().getFullYear()} {institution ? `${institution.name} · ` : ""}{PRODUCT_NAME}
          </p>
        </div>
      </div>

      {/* ── Right: sign-in form ── */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          {institution?.logoUrl && (
            <div className="lg:hidden mb-8 flex items-center justify-center">
              <img src={institution.logoUrl} alt={institution.name} className="h-11 w-auto object-contain" />
            </div>
          )}

          <div className="mb-8">
            <h1
              className="text-4xl font-extrabold text-slate-900"
            >
              {institution?.name || PRODUCT_NAME}
            </h1>
            <p className="text-slate-500 mt-2 text-[15px]" data-testid="login-subtitle">
              {institution ? `Sign in to ${PRODUCT_NAME}.` : "Welcome back! Please sign in to your account."}
            </p>
            {institution && (
              <button
                type="button"
                onClick={() => setNotMine(true)}
                className="mt-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                data-testid="login-not-mine"
              >
                Not your institution?
              </button>
            )}
          </div>

          {error && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* User ID */}
            <div>
              <label htmlFor="identifier" className="block text-sm font-semibold text-slate-700 mb-1.5">
                User ID
              </label>
              <div className="relative group">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none">
                  <Mail className="h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                </span>
                <input
                  id="identifier"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.identifier}
                  onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                  placeholder="Enter User ID"
                  className="block w-full pl-11 pr-4 py-3.5 border border-slate-200 rounded-xl bg-white
                             placeholder-slate-400 text-slate-900
                             focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500
                             transition"
                />
              </div>
              {identifierHasEmail && (
                <p className="text-xs text-red-500 mt-1">{emailLoginBlockedMessage}</p>
              )}
            </div>

            {askInstitution && (
              <div>
                <label htmlFor="institutionCode" className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Institution code
                </label>
                {notMine && !needsInstitution && (
                  <p className="text-xs text-slate-500 mb-1.5">Your institution gives you its code with your User ID.</p>
                )}
                <input
                  id="institutionCode"
                  type="text"
                  autoComplete="organization"
                  data-testid="login-institution-code"
                  placeholder="e.g. KIITX"
                  value={formData.institutionCode}
                  onChange={(e) => setFormData({ ...formData, institutionCode: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 uppercase"
                />
              </div>
            )}

            {/* Password */}
            <div>
              <div className="flex justify-between items-baseline mb-1.5">
                <label htmlFor="password" className="block text-sm font-semibold text-slate-700">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                  Forgot password?
                </Link>
              </div>
              <div className="relative group">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                </span>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="block w-full pl-11 pr-11 py-3.5 border border-slate-200 rounded-xl bg-white
                             placeholder-slate-400 text-slate-900
                             focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500
                             transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || identifierHasEmail}
              className={`w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl text-base font-semibold text-white transition-all duration-200 ${
                loading || identifierHasEmail
                  ? "bg-slate-300 cursor-not-allowed"
                  : "hover:shadow-[0_18px_40px_-15px_rgba(99,102,241,0.55)] active:translate-y-[1px]"
              }`}
              style={
                loading || identifierHasEmail
                  ? undefined
                  : { background: "linear-gradient(135deg, #4338ca 0%, #4f46e5 50%, #6366f1 100%)" }
              }
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {/* Whom to ask: the institution's own support contact (4.1) */}
          <SupportContactView
            contact={institution?.support}
            lead="Need help signing in?"
            className="mt-6 text-sm text-slate-500"
            testId="login-support"
          />

          {/* Important PDF Guides */}
          <div className="mt-8 pt-5 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2.5">
              Important PDF Guides
            </p>
            <div className="flex flex-wrap gap-2">
              {pdfGuides.map((g) => (
                <a
                  key={g.href}
                  href={g.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white
                             text-xs font-medium text-slate-600 hover:text-indigo-700 hover:border-indigo-300
                             hover:bg-indigo-50/60 transition"
                >
                  <FileText className="w-3.5 h-3.5 text-indigo-600" />
                  {g.label}
                </a>
              ))}
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-400">Protected by {PRODUCT_NAME} · Secure sign‑on</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
